import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  ConditionalRule,
  ConditionsList,
  DocumentGenerationEntry,
  DocumentGenerationEntryForm
} from '@ee/common/models';
import { ConditionEvaluatorService } from './condition-evaluator.service';

/**
 * Shared logic for pre-populating the interactive Step_Completion_Control's override
 * selector from a step's Document_Generation_List.
 *
 * It adapts a persisted Conditional_Rule to the shape the shared
 * {@link ConditionEvaluatorService} consumes, evaluates each entry's rule against the
 * current case data, and seeds a `FormArray` with the qualifying default set.
 *
 * Factored here so both the eviction `CompleteWorkflowStepComponent` (task 13.3) and
 * the Case `CaseCompleteStepComponent` (task 14) reuse it unchanged.
 */
@Injectable({ providedIn: 'root' })
export class DocumentGenerationSelectionService {
  constructor(private readonly conditionEvaluator: ConditionEvaluatorService) {}

  /**
   * Adapt a persisted `conditional_rule.conditions` (`{ source_field, operator, value }[]`)
   * to the `ConditionsList` shape (`{ field, opp, val }[]`) that
   * {@link ConditionEvaluatorService.evaluateConditions} consumes.
   *
   * An empty or absent rule maps to an empty list, which the evaluator treats as true
   * (Req 5.4 / 11.8). A condition whose field reference is absent maps to an empty
   * field path, which the evaluator resolves to `undefined` → false (Req 5.5).
   */
  toConditionsList(rule: ConditionalRule | null | undefined): ConditionsList {
    if (!rule?.conditions?.length) {
      return [];
    }
    return rule.conditions.map((condition) => ({
      field: condition.source_field?.field ?? '',
      opp: condition.operator,
      val: condition.value
    }));
  }

  /**
   * Whether a single entry qualifies for auto-selection: an entry with no
   * Conditional_Rule always qualifies (Req 5.4); an entry with a rule qualifies only
   * when the rule evaluates true against the case data (Req 5.2 / 5.3 / 5.5).
   */
  qualifies(entry: DocumentGenerationEntry, caseData: any): boolean {
    if (!entry.conditional_rule) {
      return true;
    }
    return this.conditionEvaluator.evaluateConditions(this.toConditionsList(entry.conditional_rule), caseData ?? {});
  }

  /**
   * The rule-evaluated default set: the qualifying entries, preserving order
   * (Req 5.1–5.6). Returns an empty list when nothing qualifies.
   */
  selectDefaultEntries(entries: DocumentGenerationEntry[] | null | undefined, caseData: any): DocumentGenerationEntry[] {
    return (entries ?? []).filter((entry) => this.qualifies(entry, caseData));
  }

  /** Build a typed entry form from a persisted entry, carrying its `hide_from_client`. */
  buildEntryForm(entry: DocumentGenerationEntry): DocumentGenerationEntryForm {
    return new FormGroup({
      document_template_id: new FormControl(entry.document_template_id, { nonNullable: true }),
      hide_from_client: new FormControl(entry.hide_from_client ?? false, { nonNullable: true }),
      conditional_rule: new FormControl<ConditionalRule | null>(entry.conditional_rule ?? null)
    });
  }

  /**
   * Replace the contents of the override selector's `FormArray` with the rule-evaluated
   * default set, so the user opens the control pre-populated with the qualifying entries
   * (and their `hide_from_client`) and can then add / remove / toggle before submitting.
   */
  seedDefaultSelection(
    target: FormArray<DocumentGenerationEntryForm>,
    entries: DocumentGenerationEntry[] | null | undefined,
    caseData: any
  ): void {
    target.clear();
    this.selectDefaultEntries(entries, caseData).forEach((entry) => target.push(this.buildEntryForm(entry)));
  }
}
